import { useCallback, useEffect, useState } from 'react';
import {
  collection,
  query,
  where,
  serverTimestamp,
  doc,
  deleteDoc,
  setDoc,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { logReviewAdded, logReviewUpdated, logReviewRemoved } from '../utils/hairstylistAnalytics';
import { DEFAULT_PROVIDER_TYPE } from '../config/providerTypes';

export function useReviews(hairstylistId) {
  const { currentUser } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [averageRating, setAverageRating] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);
  const [userReview, setUserReview] = useState(null);

  const updateRatingSummary = useCallback(
    async (average, count) => {
      if (!hairstylistId) return;
      try {
        await setDoc(
          doc(db, 'hairstylists', hairstylistId),
          {
            ratingSummary: {
              averageRating: average,
              reviewCount: count,
            },
          },
          { merge: true },
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error updating rating summary:', error);
      }
    },
    [hairstylistId],
  );

  useEffect(() => {
    if (!hairstylistId) {
      setReviews([]);
      setAverageRating(0);
      setReviewCount(0);
      setUserReview(null);
      return;
    }

    setLoading(true);

    const q = query(
      collection(db, 'reviews'),
      where('hairstylistId', '==', hairstylistId),
    );

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        const reviewsData = snapshot.docs.map((snap) => ({
          id: snap.id,
          ...snap.data(),
        }));
        setReviews(reviewsData);

        // Review de l'utilisateur courant
        if (currentUser) {
          const currentUserReview = reviewsData.find(
            (review) => review.userId === currentUser.uid,
          );
          setUserReview(currentUserReview || null);
        } else {
          setUserReview(null);
        }

        // Calcul moyenne + compteur
        if (reviewsData.length > 0) {
          const totalRating = reviewsData.reduce(
            (sum, review) => sum + (review.rating || 0),
            0,
          );
          const average = totalRating / reviewsData.length;
          setAverageRating(average);
          setReviewCount(reviewsData.length);
          await updateRatingSummary(average, reviewsData.length);
        } else {
          setAverageRating(0);
          setReviewCount(0);
          await updateRatingSummary(0, 0);
        }

        setLoading(false);
      },
      (error) => {
        // eslint-disable-next-line no-console
        console.error('Error listening to reviews:', error);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [hairstylistId, currentUser, updateRatingSummary]);

  const addOrUpdateReview = async (rating, comment) => {
    if (!currentUser) {
      throw new Error('Vous devez être connecté pour noter');
    }

    if (!hairstylistId) {
      throw new Error('ID coiffeuse manquant');
    }

    try {
      const reviewId = `${currentUser.uid}_${hairstylistId}`;
      const reviewData = {
        hairstylistId,
        userId: currentUser.uid,
        userName: currentUser.displayName || 'Utilisateur anonyme',
        rating,
        comment,
        providerType: DEFAULT_PROVIDER_TYPE, // Type de prestataire (par défaut 'hair')
        updatedAt: serverTimestamp(),
      };

      // Utiliser setDoc avec merge pour créer ou mettre à jour
      const isUpdate = !!userReview;
      
      if (isUpdate) {
        // Mise à jour
        await setDoc(doc(db, 'reviews', reviewId), reviewData, { merge: true });
        
        // Log review update event
        logReviewUpdated(hairstylistId, currentUser.uid, rating);
      } else {
        // Création
        reviewData.createdAt = serverTimestamp();
        await setDoc(doc(db, 'reviews', reviewId), reviewData);
        
        // Log review added event
        logReviewAdded(hairstylistId, currentUser.uid, rating);
      }
      // La mise à jour de l'état (reviews, résumé) est gérée par onSnapshot
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error adding/updating review:', error);
      throw error;
    }
  };

  const deleteReview = async () => {
    if (!currentUser) {
      throw new Error('Vous devez être connecté pour supprimer une review');
    }

    if (!userReview) {
      throw new Error('Aucune review à supprimer');
    }

    try {
      await deleteDoc(doc(db, 'reviews', userReview.id));
      setUserReview(null);
      
      // Log review removed event
      logReviewRemoved(hairstylistId, currentUser.uid);
      
      // Le listener onSnapshot mettra à jour la liste et le résumé
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error deleting review:', error);
      throw error;
    }
  };

  return { reviews, loading, averageRating, reviewCount, userReview, addOrUpdateReview, deleteReview };
}

